import Box from '@Atoms/Box';
import DateDisplay from '@Atoms/DateDisplay';
import Dialog from '@Atoms/Dialog';
import FormControl from '@Atoms/FormControl';
import InformationAlert from '@Atoms/InformationAlert';
import { DatePicker } from '@mui/x-date-pickers';
import { useCurrentCourse } from '@reducers/courses/hooks';
import { useCurrentSection } from '@reducers/sections/hooks';
import { formatCalendarDate, parseCalendarDate } from '@utils/datetime';
import React from 'react';
import { useAddTestSessionMutation, useEditTestSessionMutation } from '../../mutations';
import { AddTestSessionData, MappedTestSession } from '../../types';

const dateFormat = 'MMM dd, yyyy';
const SESSION_LENGTH_TYPE = 1;
/** Chronological date order: PT1 (TEST_1=0) → PT2 (TEST_2=2) → PT3 (TEST_3=1) */
const TEST_ORDER = [0, 2, 1];
const TEST_3 = 1;
/** session_type → practice_test_order (PT label slot) */
const PRACTICE_ORDER_BY_SESSION_TYPE: Record<number, number> = {
  0: 0,
  2: 1,
  1: 2,
};

type TestSessionDialogMode = 'add' | 'edit';

type Props = {
  open: boolean;
  mode: TestSessionDialogMode;
  testSession: MappedTestSession | null;
  siblingSessions?: MappedTestSession[];
  onClose: () => void;
  onSuccess?: () => void;
};

type DateErrors = {
  startDate?: string;
  endDate?: string;
};

const laterDate = (a?: Date, b?: Date): Date | undefined => {
  if (!a) {
    return b;
  }
  if (!b) {
    return a;
  }
  return a > b ? a : b;
};

const earlierDate = (a?: Date, b?: Date): Date | undefined => {
  if (!a) {
    return b;
  }
  if (!b) {
    return a;
  }
  return a < b ? a : b;
};

const getSessionType = (session: Pick<MappedTestSession, 'session_type' | 'practice_test_order'>) =>
  session.session_type ?? session.practice_test_order;

/** Third add is always PT3 (TEST_3); first/second use TEST_ORDER. */
const getNextSessionType = (siblingSessions: MappedTestSession[]): number => {
  if (siblingSessions.length >= 2) {
    return TEST_3;
  }
  const existingTypes = new Set(siblingSessions.map(getSessionType));
  return (
    TEST_ORDER.find((type) => !existingTypes.has(type)) ?? TEST_ORDER[TEST_ORDER.length - 1]
  );
};

const getPracticeOrder = (
  session: Pick<MappedTestSession, 'session_type' | 'practice_test_order'>,
) => {
  if (session.practice_test_order !== undefined && session.practice_test_order !== null) {
    return session.practice_test_order;
  }
  return PRACTICE_ORDER_BY_SESSION_TYPE[getSessionType(session)] ?? 0;
};

const getNeighborBoundsForSessionType = (
  siblingSessions: MappedTestSession[],
  sessionType: number,
  excludeId?: number,
) => {
  const position = PRACTICE_ORDER_BY_SESSION_TYPE[sessionType] ?? sessionType;
  const otherSessions = siblingSessions.filter((session) => session.id !== excludeId);

  let neighborMin: Date | undefined;
  let neighborMax: Date | undefined;

  otherSessions.forEach((session) => {
    const sessionPosition = getPracticeOrder(session);
    const sessionStart = parseCalendarDate(session.start_date) ?? undefined;
    if (!sessionStart) {
      return;
    }
    if (sessionPosition < position) {
      neighborMin = laterDate(neighborMin, sessionStart);
    }
    if (sessionPosition > position) {
      neighborMax = earlierDate(neighborMax, sessionStart);
    }
  });

  return { neighborMin, neighborMax };
};

const getDefaultAddSessionDate = (
  sectionStartDate?: string | null,
  sectionEndDate?: string | null,
  lastSessionDate?: Date,
): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minDate = laterDate(parseCalendarDate(sectionStartDate) ?? undefined, lastSessionDate);
  const maxDate = parseCalendarDate(sectionEndDate);

  if (minDate && today < minDate) {
    return minDate;
  }
  if (maxDate && today > maxDate) {
    return maxDate;
  }
  return today;
};

const getSessionDateBounds = (
  mode: TestSessionDialogMode,
  testSession: MappedTestSession | null,
  siblingSessions: MappedTestSession[],
  sectionStartDate?: string | null,
  sectionEndDate?: string | null,
) => {
  const sectionMin = parseCalendarDate(sectionStartDate) ?? undefined;
  const sectionMax = parseCalendarDate(sectionEndDate) ?? undefined;

  let minDate = sectionMin;
  let maxDate = sectionMax;
  let rangeTitle = 'Available Date Range';
  let neighborMin: Date | undefined;
  let neighborMax: Date | undefined;

  if (mode === 'edit' && testSession) {
    const policyMin = parseCalendarDate(testSession.policy_start_date) ?? undefined;
    const policyMax = parseCalendarDate(testSession.policy_end_date) ?? undefined;
    const hasPolicyBounds = Boolean(policyMin && policyMax);

    minDate = policyMin ?? sectionMin;
    maxDate = policyMax ?? sectionMax;
    rangeTitle = hasPolicyBounds ? 'Policy Date Range' : 'Available Date Range';

    ({ neighborMin, neighborMax } = getNeighborBoundsForSessionType(
      siblingSessions,
      getSessionType(testSession),
      testSession.id,
    ));
    minDate = laterDate(minDate, neighborMin);
    maxDate = earlierDate(maxDate, neighborMax);
  } else {
    const nextSessionType = getNextSessionType(siblingSessions);
    ({ neighborMin, neighborMax } = getNeighborBoundsForSessionType(
      siblingSessions,
      nextSessionType,
    ));
    minDate = laterDate(sectionMin, neighborMin);
    maxDate = earlierDate(sectionMax, neighborMax);
  }

  return {
    minDate,
    maxDate,
    rangeStart: minDate ?? parseCalendarDate(sectionStartDate),
    rangeEnd: maxDate ?? parseCalendarDate(sectionEndDate),
    rangeTitle,
    neighborMin,
    neighborMax,
  };
};

const buildSessionPayload = (
  startDate: Date,
  endDate: Date,
  curriculum: number,
  sectionId: number,
  sittingsNumber: number,
  sessionType: number,
): AddTestSessionData => ({
  curriculum,
  end_date: formatCalendarDate(endDate),
  length_type: SESSION_LENGTH_TYPE,
  published: false,
  section: sectionId,
  session_type: sessionType,
  sittings_number: sittingsNumber,
  start_date: formatCalendarDate(startDate),
});

const TestSessionDialog = ({
  open,
  mode,
  testSession,
  siblingSessions = [],
  onClose,
  onSuccess,
}: Props) => {
  const section = useCurrentSection();
  const course = useCurrentCourse();
  const addTestMutation = useAddTestSessionMutation();
  const editTestMutation = useEditTestSessionMutation();
  const [startDate, setStartDate] = React.useState<Date | null>(new Date());
  const [endDate, setEndDate] = React.useState<Date | null>(new Date());

  const { minDate, maxDate, rangeStart, rangeEnd, rangeTitle, neighborMin, neighborMax } =
    React.useMemo(
      () =>
        getSessionDateBounds(
          mode,
          testSession,
          siblingSessions,
          section.start_date,
          section.end_date,
        ),
      [mode, section.end_date, section.start_date, siblingSessions, testSession],
    );

  React.useEffect(() => {
    if (!open) {
      return;
    }
    if (mode === 'edit' && testSession) {
      setStartDate(parseCalendarDate(testSession.start_date) ?? new Date());
      setEndDate(parseCalendarDate(testSession.end_date) ?? new Date());
      return;
    }
    const defaultDate = getDefaultAddSessionDate(
      section.start_date,
      section.end_date,
      neighborMin,
    );
    setStartDate(defaultDate);
    setEndDate(defaultDate);
  }, [mode, neighborMin, open, section.end_date, section.start_date, testSession]);

  const isPolicyEnforced = mode === 'edit' && !!testSession?.policy_enforced;
  const errors = React.useMemo<DateErrors>(() => {
    if (!startDate || !endDate) {
      return {};
    }
    if (minDate && startDate < minDate) {
      return {
        startDate: neighborMin
          ? 'Start date must be on or after the previous test session date'
          : 'Start date must be after section start date',
      };
    }
    if (maxDate && startDate > maxDate) {
      return {
        startDate: neighborMax
          ? 'Start date must be on or before the next test session date'
          : 'Start date must be before section end date',
      };
    }
    if (endDate < startDate) {
      return { endDate: 'End date cannot be before start date' };
    }
    return {};
  }, [startDate, endDate, minDate, maxDate, neighborMin, neighborMax]);

  const hasErrors = !!errors.startDate || !!errors.endDate || !startDate || !endDate;
  const isLoading = addTestMutation.isLoading || editTestMutation.isLoading;

  const handleSave = () => {
    if (!startDate || !endDate || hasErrors || isPolicyEnforced) {
      return;
    }

    const sessionType =
      mode === 'add'
        ? getNextSessionType(siblingSessions)
        : getSessionType(testSession as MappedTestSession);
    const payload = buildSessionPayload(
      startDate,
      endDate,
      course.curriculum,
      section.id,
      section.sittings_number,
      sessionType,
    );
    const mutationOptions = {
      onSuccess: () => {
        onSuccess?.();
        onClose();
      },
    };

    if (mode === 'edit' && testSession) {
      editTestMutation.mutate({ id: testSession.id, ...payload }, mutationOptions);
      return;
    }

    addTestMutation.mutate(payload, mutationOptions);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={mode === 'add' ? 'Add Test Session' : 'Edit Test Session'}
      subtitle={
        mode === 'add'
          ? 'Set start and end dates for the new test session'
          : 'Update the test session configuration'
      }
      actions={
        isPolicyEnforced
          ? [
              {
                label: 'Close',
                buttonProps: { variant: 'contained' },
                onClick: onClose,
              },
            ]
          : [
              {
                label: 'Cancel',
                buttonProps: { variant: 'outlined' },
                onClick: onClose,
              },
              {
                label: mode === 'add' ? 'Add Session' : 'Save',
                buttonProps: {
                  variant: 'contained',
                  disabled: hasErrors,
                  isLoading,
                },
                onClick: handleSave,
              },
            ]
      }
    >
      <Box sx={{ p: 3, minWidth: '500px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {isPolicyEnforced && (
          <InformationAlert title="Policy dates are enforced">
            Contact your administrator to change these dates.
          </InformationAlert>
        )}
        <FormControl errorMessage={errors.startDate} fullWidth>
          <DatePicker
            label="Start Date"
            minDate={minDate}
            maxDate={maxDate}
            disabled={isPolicyEnforced}
            format={dateFormat}
            onChange={(value) => {
              setStartDate(value);
            }}
            value={startDate}
          />
        </FormControl>
        <FormControl errorMessage={errors.endDate} fullWidth>
          <DatePicker
            label="End Date"
            minDate={startDate ?? undefined}
            disabled={isPolicyEnforced}
            format={dateFormat}
            onChange={(value) => {
              setEndDate(value);
            }}
            value={endDate}
          />
        </FormControl>
        {rangeStart && rangeEnd && (
          <InformationAlert title={rangeTitle}>
            <DateDisplay
              startDate={rangeStart}
              endDate={rangeEnd}
              variant="elegant"
              size="small"
              showIcon
              color="primary"
            />
          </InformationAlert>
        )}
      </Box>
    </Dialog>
  );
};

export default TestSessionDialog;
